// ─────────────────────────────────────────────────────────────────────────
// batch-65 uitrusting-flow (FO-480) — levering of afhalen inplannen
//
// Na het verwerken van een uitrustingbestelling ontstaat een lever-/afhaal-
// afspraak (`UitrustingLevering`) met een voorgesteld moment. Het magazijn
// bevestigt of past aan, de medewerker kan kiezen uit 2-3 voorgestelde
// alternatieven (geen vrije tekst), de planner ziet een markering op de
// dagregel, en de bestelling gaat pas naar GELEVERD bij de overdracht.
//
// Dit bestand bevat de gedeelde bouwstenen: statusunions + labels, de DTO's
// (portal, backoffice en planbord) en de zod-schemas van de mutaties (API
// `.safeParse` én client-side validatie in de formulieren).
//
// Conventies (gelijk aan de rest van packages/types):
//   - Een kalenderdag gaat als `yyyy-mm-dd` over de wire (NL-kalenderdag), een
//     tijd als "HH:mm" (NL-tijd). Formatteren doet de UI.
//   - Enums als string-unions die de Prisma-enums spiegelen.
// ─────────────────────────────────────────────────────────────────────────

import { z } from 'zod'

// ─── Enums (spiegelen de Prisma-enums) ──────────────────────────────────────

export const UITRUSTING_LEVER_WIJZEN = ['AFHALEN', 'MEEGEVEN', 'THUIS', 'OP_LOCATIE'] as const
export type UitrustingLeverWijze = (typeof UITRUSTING_LEVER_WIJZEN)[number]

export const UITRUSTING_LEVER_WIJZE_LABELS: Record<UitrustingLeverWijze, string> = {
  AFHALEN: 'Afhalen',
  MEEGEVEN: 'Meegeven via een collega',
  THUIS: 'Thuis bezorgen',
  OP_LOCATIE: 'Op locatie bij de klant',
}

export const UITRUSTING_LEVERING_STATUSSEN = [
  'TE_PLANNEN',
  'GEPLAND',
  'KLAAR_VOOR_OVERDRACHT',
  'GELEVERD',
  'NIET_VERSCHENEN',
  'VERVALLEN',
] as const
export type UitrustingLeveringStatus = (typeof UITRUSTING_LEVERING_STATUSSEN)[number]

export const UITRUSTING_LEVERING_STATUS_LABELS: Record<UitrustingLeveringStatus, string> = {
  TE_PLANNEN: 'Te plannen',
  GEPLAND: 'Gepland',
  KLAAR_VOOR_OVERDRACHT: 'Klaar voor overdracht',
  GELEVERD: 'Geleverd',
  NIET_VERSCHENEN: 'Niet verschenen',
  VERVALLEN: 'Vervallen',
}

/** Badge-variant per status, zodat werkvoorraad en detail dezelfde kleur tonen. */
export const UITRUSTING_LEVERING_STATUS_BADGE: Record<
  UitrustingLeveringStatus,
  'secondary' | 'info' | 'warning' | 'success' | 'danger'
> = {
  TE_PLANNEN: 'warning',
  GEPLAND: 'info',
  KLAAR_VOOR_OVERDRACHT: 'info',
  GELEVERD: 'success',
  NIET_VERSCHENEN: 'danger',
  VERVALLEN: 'secondary',
}

/** Statussen waarin een afspraak nog loopt (nog over te dragen). */
export const UITRUSTING_LEVERING_OPEN_STATUSSEN = [
  'TE_PLANNEN',
  'GEPLAND',
  'KLAAR_VOOR_OVERDRACHT',
] as const satisfies readonly UitrustingLeveringStatus[]

export function isLeveringOpen(status: UitrustingLeveringStatus): boolean {
  return (UITRUSTING_LEVERING_OPEN_STATUSSEN as readonly string[]).includes(status)
}

export const UITRUSTING_LEVERING_DELEN = ['VOORRAAD', 'EXTERN'] as const
export type UitrustingLeveringDeel = (typeof UITRUSTING_LEVERING_DELEN)[number]

/**
 * Na zoveel keer niet verschenen in één keten krijgt de beoordelaar een signaal
 * (spec §2.6, T5). Elke gemiste afspraak levert daarnaast gewoon een nieuw
 * voorstel voor de medewerker op.
 */
export const LEVERING_NIET_VERSCHENEN_DREMPEL = 2

/** Waarop een (voorgesteld) moment gebaseerd is. HANDMATIG = gekozen door het magazijn. */
export const LEVER_MOMENT_SOORTEN = [
  'KANTOORDAG',
  'VRIJE_DAG',
  'VOOR_DIENST',
  'NA_DIENST',
  'HANDMATIG',
] as const
export type LeverMomentSoort = (typeof LEVER_MOMENT_SOORTEN)[number]

// ─── DTO's ──────────────────────────────────────────────────────────────────

/** Eén voorgesteld (of vastgelegd) moment om de spullen over te dragen. */
export interface LeverMomentDTO {
  /** yyyy-mm-dd (NL-kalenderdag). */
  datum: string
  /** "HH:mm" (NL-tijd). */
  tijdVan: string
  tijdTot: string
  soort: LeverMomentSoort
  /** Korte uitleg voor de medewerker, bv. "Je bent die dag op kantoor". */
  reden: string
}

export interface UitrustingLeveringRegelDTO {
  id: string
  bestellingId: string
  regelId: string
  deel: UitrustingLeveringDeel
  aantal: number
  /** Bij de overdracht echt afgemeld. */
  aantalGeleverd: number
  /** Uit de bevroren snapshot van de bestelregel. */
  naam: string
  maat: string | null
  kleur: string | null
}

export interface UitrustingLeveringTijdlijnDTO {
  id: string
  action: string
  fromStatus: string | null
  toStatus: string | null
  performedByNaam: string | null
  note: string | null
  createdAt: string
}

/**
 * Een lever-/afhaalafspraak. Dezelfde vorm voor portal en backoffice: er staan
 * geen prijzen, leveranciers of andere interne gegevens in; de plek waar de
 * medewerker ophaalt hoort er juist wel bij ("bij {locatie}").
 */
export interface UitrustingLeveringDTO {
  id: string
  employeeId: string
  employeeNaam: string
  wijze: UitrustingLeverWijze
  status: UitrustingLeveringStatus
  /** Het vastgelegde moment (vanaf GEPLAND); null zolang het nog te plannen is. */
  moment: { datum: string; tijdVan: string | null; tijdTot: string | null } | null
  /** Het systeemvoorstel; null als er in de komende werkdagen geen moment past. */
  voorstel: LeverMomentDTO | null
  /** Waaruit de medewerker mag kiezen (alleen dagen vanaf vandaag). */
  alternatieven: LeverMomentDTO[]
  /** yyyy-mm-dd: vanaf wanneer de spullen klaar kunnen liggen. */
  klaarVanaf: string
  locatieNaam: string | null
  voorraadLocatieId: string | null
  clientLocationId: string | null
  meenemerEmployeeId: string | null
  meenemerNaam: string | null
  notitie: string | null
  /** True wanneer de medewerker zelf een van de voorgestelde momenten koos. */
  gekozenDoorMedewerker: boolean
  geplandOp: string | null
  klaargezetOp: string | null
  geleverdOp: string | null
  geleverdDoorNaam: string | null
  ontvangenDoorNaam: string | null
  heeftHandtekening: boolean
  nietVerschenenOp: string | null
  /** Aantal keer niet verschenen in deze keten tot en met deze afspraak. */
  nietVerschenenTeller: number
  vorigeLeveringId: string | null
  /** Na NIET_VERSCHENEN: de afspraak die hierop volgde. */
  volgendeLeveringId: string | null
  vervallenReden: string | null
  regels: UitrustingLeveringRegelDTO[]
  /** Stuks die nog over te dragen zijn (0 bij een afgeronde afspraak). */
  openStuks: number
  /** Alleen op het detail. */
  tijdlijn?: UitrustingLeveringTijdlijnDTO[]
}

// ─── Werkvoorraad (backoffice) ──────────────────────────────────────────────

/** Tabs van de werkvoorraad "Leveringen" (spec §2.7). */
export const LEVERING_WERKVOORRAAD_TABS = [
  'te-plannen',
  'gepland-vandaag',
  'klaar',
  'geleverd',
  // Gemiste en vervallen afspraken: de werkvoorraad toont zo alle statussen.
  'overig',
] as const
export type LeveringWerkvoorraadTab = (typeof LEVERING_WERKVOORRAAD_TABS)[number]

export const LEVERING_WERKVOORRAAD_TAB_LABELS: Record<LeveringWerkvoorraadTab, string> = {
  'te-plannen': 'Te plannen',
  'gepland-vandaag': 'Gepland vandaag',
  klaar: 'Klaar staan',
  geleverd: 'Geleverd',
  overig: 'Niet verschenen of vervallen',
}

export interface LeveringWerkvoorraadMetaDTO {
  total: number
  page: number
  pageSize: number
  /** Servertelling per tab over de volledige gefilterde set (niet de pagina). */
  tellers: Record<LeveringWerkvoorraadTab, number>
  /** yyyy-mm-dd: de dag die "Gepland vandaag" toont (standaard vandaag). */
  datum: string
}

/** Een magazijn- of kantoorlocatie waar afgehaald kan worden (korte vaste lijst). */
export interface LeverLocatieOptieDTO {
  id: string
  naam: string
  type: string
}

// ─── Planbord (FO-480 §A.4) ─────────────────────────────────────────────────

/**
 * Een lopende afspraak op de dagregel van een medewerker op het planbord: een
 * klein icoon "uitrusting meenemen". Bewust NIET via `leaveBlocks`: elke
 * consument van die banden (vervanging, beschikbaarheid, Excel-verloftab,
 * mobiel bord, klantdagen) leest een band als afwezigheid, en een afhaalmoment
 * is geen afwezigheid.
 */
export interface PlanningBoardUitrustingAfspraakDTO {
  id: string
  /** yyyy-mm-dd: het vastgelegde moment, of het voorstel zolang TE_PLANNEN. */
  datum: string
  /** True zolang het een voorstel is (nog niet bevestigd). */
  voorstel: boolean
  wijze: UitrustingLeverWijze
  status: UitrustingLeveringStatus
  tijdVan: string | null
  tijdTot: string | null
  locatieNaam: string | null
  aantalStuks: number
  /** ONTVANGER = deze rij krijgt de spullen; MEENEMER = deze rij neemt ze mee voor een collega. */
  rol: 'ONTVANGER' | 'MEENEMER'
  /** De andere partij: bij ONTVANGER de meenemer, bij MEENEMER de ontvanger. */
  anderePartijNaam: string | null
}

// ─── Request-schemas (gedeeld door API en formulieren) ──────────────────────

const DATUM = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies een datum')
const TIJD = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Gebruik uu:mm')

/**
 * Het magazijn legt het moment vast (bevestigen van het voorstel of een eigen
 * keuze). AFHALEN vraagt een afhaallocatie; MEEGEVEN en OP_LOCATIE een
 * meenemer. Bij OP_LOCATIE leidt de API de klantlocatie af uit de planning van
 * die dag (één waarheid, geen tweede invoer).
 */
export const LeveringPlanSchema = z
  .object({
    datum: DATUM,
    tijdVan: TIJD,
    tijdTot: TIJD,
    wijze: z.enum(UITRUSTING_LEVER_WIJZEN),
    voorraadLocatieId: z.string().min(1).nullable().optional(),
    meenemerEmployeeId: z.string().min(1).nullable().optional(),
    notitie: z.string().max(2000).nullable().optional(),
  })
  .refine((v) => v.tijdVan < v.tijdTot, {
    message: 'De eindtijd moet na de begintijd liggen',
    path: ['tijdTot'],
  })
  .refine((v) => v.wijze !== 'AFHALEN' || Boolean(v.voorraadLocatieId), {
    message: 'Kies waar de medewerker het ophaalt',
    path: ['voorraadLocatieId'],
  })
  .refine(
    (v) => (v.wijze !== 'MEEGEVEN' && v.wijze !== 'OP_LOCATIE') || Boolean(v.meenemerEmployeeId),
    { message: 'Kies wie het meeneemt', path: ['meenemerEmployeeId'] },
  )
export type LeveringPlanInput = z.infer<typeof LeveringPlanSchema>

/** De medewerker kiest een van de voorgestelde momenten (geen vrije invoer). */
export const LeveringKiesMomentSchema = z.object({
  datum: DATUM,
  tijdVan: TIJD,
  tijdTot: TIJD,
})
export type LeveringKiesMomentInput = z.infer<typeof LeveringKiesMomentSchema>

/** Overdracht afmelden: alles wat in de afspraak zit in één keer. */
export const LeveringOverdrachtSchema = z.object({
  /** Optioneel: handtekening van de ontvanger (PNG als data-URL). */
  handtekeningData: z
    .string()
    .max(600_000, 'De handtekening is te groot')
    .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'Ongeldige handtekening')
    .nullable()
    .optional(),
  ontvangenDoorNaam: z.string().trim().max(200).nullable().optional(),
  notitie: z.string().max(2000).nullable().optional(),
  /**
   * Alleen afsluiten: er is niets meer over te dragen. Staat er intussen toch
   * iets open (net toegevoegd), dan weigert de API in plaats van een overdracht
   * te registreren die niet heeft plaatsgevonden.
   */
  alleenAfsluiten: z.boolean().optional(),
})
export type LeveringOverdrachtInput = z.infer<typeof LeveringOverdrachtSchema>

export const LeveringNietVerschenenSchema = z.object({
  notitie: z.string().max(2000).nullable().optional(),
})
export type LeveringNietVerschenenInput = z.infer<typeof LeveringNietVerschenenSchema>

/**
 * Het extern bestelde deel van een regel is op kantoor binnengekomen en kan
 * worden overgedragen. Leeg = alles wat van het externe deel nog openstaat.
 */
export const RegelOntvangenSchema = z.object({
  aantal: z.number().int().min(1).optional(),
})
export type RegelOntvangenInput = z.infer<typeof RegelOntvangenSchema>
